import { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContentText,
  styled,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { TrashIcon, PlusIcon, PenIcon } from '../components/svgicons/SvgIcons';
import FancyTooltip from '../components/utils/FancyTooltip';
import FancyPaper from '../components/FancyPaper';
import { useForm, Controller } from 'react-hook-form';

import { properties } from '../utils/Properties_es';
import { useApp } from '../hooks/useApp';
import DeleteModal from '../components/utils/DeleteModal';
import ResourceAccess from '../components/security/ResourceAccess';
import FancyTablePagination from '../components/utils/FancyTablePagination';
import AddOrUpdateEndUsersModal from '../components/utils/AddOrUpdateEndUsers';
import {
  createEndUser,
  deleteEndUser,
  getEndUsers,
  getUserWithAseet,
  updateEndUser,
} from '../services/endUser/EndUserService';
import { BodyEndUser, ContentEndUser, EndUserResponse } from '../types/EndUser';
import Loading from '../components/utils/Loading';
import { Box, TextField } from '@mui/material';

const Title = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#e1e8ee',
  color: '#3e627c!important',
  fontFamily: 'D-dinExp',
  fontWeight: 400,
  fontSize: '17.5px',
}));
const Text = styled(DialogContentText)(({ theme }) => ({
  color: theme.palette.greyDue.main,
  fontFamily: 'D-dinExp',
  fontWeight: 400,
  textAlign: 'center',
  fontSize: '17px',
  padding: theme.spacing(4),
}));
const Actions = styled(DialogActions)(({ theme }) => ({
  borderTop: '1px solid #dee2e6',
}));

const initForm = {
  id: 0,
  user: '',
  name: '',
  lastName: '',
  phoneNumber: '',
  mail: '',
};
type FormData = typeof initForm;

const EndUsersManagement = () => {
  const {
    isLoading,
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    setSuccessMsg,
    errorMsg,
    authInfo,
  } = useApp();
  const theme = useTheme();

  const defaultValues = {
    search: '',
  };

  const [endUserData, setEndUserData] = useState<EndUserResponse>({} as EndUserResponse);
  const [page, setPage] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>(initForm);
  const { handleSubmit, control, setValue, watch } = useForm();
  const [preFilter, setPreFilter] = useState(defaultValues);
  const [openModalConfirmDelete, setOpenModalConfirmDelete] = useState(false);
  const [assetsUser, setAssetsUser] = useState(0);

  const handleCancelModalConfirmDelete = () => {
    setOpenModalConfirmDelete(false);
  };

  /**
   * Efecto inicial para carga de registros
   */
  useEffect(() => {
    const dataInit = async () => {
      await handleFetchData(0);
    };
    dataInit();

    // eslint-disable-next-line
  }, []);

  /**
   * Metodo encargado de buscar registros
   * @param filter
   * @param currentPage
   */
  const handleFetchData = async (currentPage?: number, filtro?: string) => {
    setLoading && setLoading(true);
    try {
      setPage(currentPage || 0);

      let data = await getEndUsers(currentPage || 0, 10, filtro);
      if (data) {
        setEndUserData(data);
      }
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
      console.log(error);
    }
  };

  /**
   * Metodo encargado de crear registro
   * @param data
   */
  const handleAdd = async (data: BodyEndUser) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let createData = await createEndUser({ ...data, status: 'ACTIVE' });

      if (!createData) {
        setErrorMsg && setErrorMsg(properties.com_parval_label_enduser_save_error);
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg(properties.com_parval_label_request_update);
      //call fetch data
      await handleFetchData();
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
      console.log(error);
    }
  };

  /**
   * Metodo encargado de actualizar registro
   * @param data
   */
  const handleUpdate = async (data: FormData): Promise<void> => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      const updateData = await updateEndUser({ ...data, status: 'ACTIVE' });

      if (!updateData) {
        setErrorMsg && setErrorMsg(properties.com_parval_label_enduser_update_error);
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg(properties.com_parval_label_request_update);
      //call fetch data
      await handleFetchData();
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  /**
   * Metodo encargado de eliminar registro
   * @param data
   */
  const handleDelete = async (id: number) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let deleteData = await deleteEndUser(id, authInfo ? authInfo.username : 'Prueba');
      if (!deleteData) {
        setErrorMsg && setErrorMsg(properties.com_parval_label_enduser_delete_error);
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg &&
        setSuccessMsg(
          assetsUser
            ? 'Activos de usuario eliminado se trasladaron a estado pendiente de asignación'
            : properties.com_parval_label_request_delete
        );
      setOpenModalConfirmDelete(false);

      //reset page and call fetch data
      await handleFetchData();
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
      setOpenModalConfirmDelete(false);
    }
  };

  /**
   * Efecto para validar errores en caso de abrir modal
   */
  useEffect(() => {
    if (modalData && modalData?.modalOpen && errorMsg) {
      setModalData &&
        setModalData({
          modalOpen: false,
          modalType: '',
          modalObject: null,
        });
    }
  }, [modalData, errorMsg, setModalData]);

  /**
   * Evento de apertura de modal
   */

  type ModalAction = 'update' | 'delete';
  const handleOpenModal = async (event: any) => {
    event.preventDefault();
    const modalAction = event.currentTarget.getAttribute('data-name') as ModalAction;
    let object = null;
    const id = event.currentTarget.getAttribute('data-id');
    setAssetsUser(0);

    let userWithAssets = false;

    if (modalAction === 'update') {
      object = endUserData.content.find((p: any) => p.id === parseInt(id));
      //@ts-ignore
      setFormData(object);
    }

    if (modalAction === 'delete') {
      object = endUserData.content.find((p: any) => p.id === parseInt(id));
      const userAssets = await getUserWithAseet(Number(object?.id));
      userWithAssets = userAssets.assignedAssets;
      if (userWithAssets) {
        setAssetsUser(userAssets.quantity);
        setOpenModalConfirmDelete(true);
      }
    }

    // open modal
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: userWithAssets && modalAction === 'delete' ? false : true,
        modalType: modalAction,
        // @ts-ignore
        modalObject: object,
      });
  };

  /**
   * @param event
   */
  const handleCancelModal = () => {
    setFormData(initForm);
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: false,
        modalType: '',
        modalObject: null,
      });
  };

  const onSubmit = async (data: any) => {
    switch (modalData?.modalType) {
      case 'create':
        await handleAdd(data);
        break;
      case 'update':
        await handleUpdate(data);
        break;
      case 'delete':
        // @ts-ignore
        await handleDelete(modalData?.modalObject?.id);
        break;
      default:
        break;
    }
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    setPage(newPage - 1);
    let customPage = newPage - 1;
    if (customPage !== page) {
      await handleFetchData(customPage, preFilter.search);
    }
  };

  const handleFilter = async (e: any) => {
    const filtro = e.search;
    setPreFilter({
      search: filtro,
    });

    if (filtro === '') {
      await handleFetchData(0);
    } else {
      await handleFetchData(0, filtro);
    }
  };

  const watchSearch = watch('search');

  const handleClearFilter = async (e: any) => {
    await handleFetchData(0);
    setPreFilter({
      search: '',
    });
    setValue('search', '');
  };

  return (
    <>
      {isLoading && <Loading />}
      <FancyPaper pagetitle='Usuarios Finales'>
        <Grid container spacing={8} sx={{ pb: 8 }}>
          <Grid item md={8} sm={6} xs={12}>
            <ResourceAccess isCode={true} pathOrCode={'ENDUSER:WRITE'}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleOpenModal}
              data-name='create'>
              Agregar Usuario Final
              <PlusIcon sx={{ ml: 1 }} />
            </Button>
            </ResourceAccess>
          </Grid>
          <Grid item md={4} sm={6} xs={12}>
            <Box display='flex' gap={4} justifyContent='flex-end'>
              <Controller
                name='search'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <TextField
                    {...field}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant='outlined'
                    size='small'
                    sx={{
                      '& label': {
                        marginTop: '-5px',
                      },
                    }}
                  />
                )}
              />

              <Button
                variant='contained'
                color='primary'
                onClick={handleSubmit(handleFilter)}
                disabled={watchSearch === '' ? true : false}>
                Buscar
              </Button>
              <Button variant='contained' color='primary' onClick={handleClearFilter}>
                Limpiar
              </Button>
            </Box>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align='center'>Usuario</TableCell>
                <TableCell align='center'>Nombre</TableCell>
                <TableCell align='center'>Apellido</TableCell>
                <TableCell align='center'>Teléfono</TableCell>
                <TableCell align='center'>Correo</TableCell>
                <TableCell align='center'>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endUserData.content &&
                endUserData.content.map((row: ContentEndUser, i: number) => (
                  <TableRow
                    key={i}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}>
                    <TableCell component='th' scope='row'>
                      {row.id}
                    </TableCell>
                    <TableCell align='center'>{row.user}</TableCell>
                    <TableCell align='center'>{row.name}</TableCell>
                    <TableCell align='center'>{row.lastName}</TableCell>
                    <TableCell align='center'>{row.phoneNumber}</TableCell>
                    <TableCell align='center'>{row.mail}</TableCell>
                    <TableCell align='center'>
                      <ResourceAccess isCode={true} pathOrCode={'ENDUSER:WRITE'}>
                        <FancyTooltip title='Editar' placement='top'>
                          <IconButton
                            aria-label='edit'
                            component='label'
                            color='secondary'
                            sx={{
                              '&:hover': {
                                color: theme.palette.secondary.dark,
                              },
                            }}
                            onClick={handleOpenModal}
                            data-name='update'
                            data-id={row.id}>
                            <PenIcon />
                          </IconButton>
                        </FancyTooltip>
                        <FancyTooltip title='Eliminar' placement='top'>
                          <IconButton
                            aria-label='trash'
                            component='label'
                            color='secondary'
                            sx={{
                              '&:hover': {
                                color: theme.palette.secondary.dark,
                              },
                            }}
                            data-id={row.id}
                            onClick={handleOpenModal}
                            data-name='delete'>
                            <TrashIcon />
                          </IconButton>
                        </FancyTooltip>
                      </ResourceAccess>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* {!!endUserData?.content?.length && ( */}
        <FancyTablePagination
          count={endUserData?.content?.length > 0 ? endUserData?.content?.length : 0}
          rowsPerPage={endUserData.size}
          page={page}
          onPageChange={handleChangePage}
          totalElements={endUserData.totalElements}
          totalPages={endUserData.totalPages}
        />
        {/* )} */}
      </FancyPaper>
      {(modalData?.modalType === 'create' || modalData?.modalType === 'update') && (
        <Dialog open={modalData.modalOpen} onClose={handleCancelModal} fullWidth>
          <AddOrUpdateEndUsersModal
            data={formData}
            onSubmit={onSubmit}
            cancelModal={handleCancelModal}
            modalType={modalData?.modalType}
          />
        </Dialog>
      )}
      {modalData?.modalType === 'delete' && (
        <Dialog open={modalData.modalOpen} onClose={handleCancelModal} fullWidth>
          <DeleteModal
            //@ts-ignore
            textChildren={modalData?.modalObject?.username}
            actionButton={
              <>
                {' '}
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  sx={{
                    mt: 2,
                    mr: 2,
                  }}
                  onClick={handleSubmit(onSubmit)}>
                  Aceptar
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  sx={{
                    mt: 2,
                  }}
                  onClick={handleCancelModal}
                  autoFocus>
                  Cancelar
                </Button>{' '}
              </>
            }
          />
        </Dialog>
      )}
      <Dialog open={openModalConfirmDelete} onClose={handleCancelModalConfirmDelete} fullWidth>
        <Title
          id='alert-dialog-title'
          sx={{
            pt: 4,
            px: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}>
          <TrashIcon />
          Eliminar usuario
        </Title>
        <DialogContent>
          <Text id='alert-dialog-description'>
            <Box
              sx={{
                textAlign: 'left',
                fontSize: '17px',
              }}>
              <Typography fontSize='inherit'>
                El usuario tiene {assetsUser}{' '}
                {assetsUser > 1 ? 'activos asignados' : 'activo asignado'}
              </Typography>
              <Typography fontSize='inherit'>¿Desea eliminar este usuario?</Typography>
            </Box>
          </Text>
        </DialogContent>
        <Actions sx={{ pb: 4, px: 4 }}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            sx={{
              mt: 2,
              mr: 2,
            }}
            //@ts-ignore
            onClick={() => handleDelete(modalData?.modalObject?.id!!)}>
            Eliminar
          </Button>
          <Button
            variant='contained'
            color='secondary'
            sx={{
              mt: 2,
            }}
            onClick={() => setOpenModalConfirmDelete(false)}>
            Cerrar
          </Button>{' '}
        </Actions>
      </Dialog>
    </>
  );
};

export default EndUsersManagement;
